import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/quotes")({
  component: QuotesPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchQuotes,
  useQuoteMutations,
} from "@/hooks/api/inventory/useSales";
import {
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { SalesFormSheet } from "@/components/inventory/forms/SalesFormSheet";
import { QuoteTable } from "@/components/inventory/tables/QuoteTable";
import { QuoteDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type { Quote, QuotePayload, QuoteStatus } from "@/types/inventory";

const STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Sent", value: "SENT" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Converted", value: "CONVERTED" },
] satisfies { label: string; value: QuoteStatus }[];

function QuotesPage() {
  const navigate = useNavigate();
  const {
    createQuote,
    updateQuote,
    rejectQuote,
    sendQuote,
    approveQuote,
    expireQuote,
    convertQuote,
  } = useQuoteMutations();
  const isMutating =
    createQuote.isPending ||
    updateQuote.isPending ||
    rejectQuote.isPending ||
    sendQuote.isPending ||
    approveQuote.isPending ||
    expireQuote.isPending ||
    convertQuote.isPending;

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);
  const [viewTarget, setViewTarget] = useState<Quote | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Quote | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Quote | null>(null);
  const [sendTarget, setSendTarget] = useState<Quote | null>(null);
  const [approveTarget, setApproveTarget] = useState<Quote | null>(null);
  const [expireTarget, setExpireTarget] = useState<Quote | null>(null);
  const [convertTarget, setConvertTarget] = useState<Quote | null>(null);

  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS, {
    enabled: isSheetOpen,
  });
  const warehouses = warehousesQuery.data?.results ?? [];
  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS, {
    enabled: isSheetOpen,
  });
  const items = itemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS, {
    enabled: isSheetOpen,
  });
  const units = unitsQuery.data?.results ?? [];

  const quotesQuery = useFetchQuotes({
    page,
    page_size: pageSize,
    status: statusFilter,
  });
  const quotes = quotesQuery.data?.results ?? [];
  const count = quotesQuery.data?.count ?? 0;

  const isLoading = quotesQuery.isPending || isMutating;
  const isFetching = quotesQuery.isFetching || isMutating;

  useEffect(() => {
    setPage(1);
  }, [statusFilter, setPage]);

  const resetFilters = () => {
    setStatusFilter(undefined);
    setPage(1);
  };

  const handleOpenSheet = () => {
    setIsSheetOpen(true);
  };

  const handleSubmit = async (payload: QuotePayload) => {
    if (editing) {
      await updateQuote.mutateAsync({ id: editing.id, payload });
      toast.success("Quote updated");
    } else {
      await createQuote.mutateAsync(payload);
      toast.success("Quote created");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    try {
      await rejectQuote.mutateAsync({ id: rejectTarget.id });
      toast.success("Quote rejected");
      setRejectTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleSendConfirm = async () => {
    if (!sendTarget) return;
    try {
      await sendQuote.mutateAsync({ id: sendTarget.id });
      toast.success("Quote sent");
      setSendTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleApproveConfirm = async () => {
    if (!approveTarget) return;
    try {
      await approveQuote.mutateAsync({ id: approveTarget.id });
      toast.success("Quote approved");
      setApproveTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleExpireConfirm = async () => {
    if (!expireTarget) return;
    try {
      await expireQuote.mutateAsync({ id: expireTarget.id });
      toast.success("Quote expired");
      setExpireTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleConvertConfirm = async () => {
    if (!convertTarget) return;
    try {
      const so = await convertQuote.mutateAsync({ id: convertTarget.id });
      toast.success("Quote converted to Sales Order");
      setConvertTarget(null);
      navigate({
        to: "/admin/inventory/sales-orders/$id",
        params: { id: String(so.id) },
      });
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Quotes"
          description="Manage customer quotes"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleOpenSheet}
          addLabel="New Quote"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        <QuoteTable
          quotes={quotes}
          isLoading={isLoading}
          onNameClick={(q) =>
            navigate({
              to: "/admin/inventory/quotes/$id",
              params: { id: String(q.id) },
            })
          }
          onView={(q) => setViewTarget(q)}
          onEdit={(q) => {
            setEditing(q);
            handleOpenSheet();
          }}
          onReject={(q) => setRejectTarget(q)}
          onSend={(q) => setSendTarget(q)}
          onApprove={(q) => setApproveTarget(q)}
          onExpire={(q) => setExpireTarget(q)}
          onConvert={(q) => setConvertTarget(q)}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <SalesFormSheet
          mode="quote"
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={items}
          units={units}
          isLoading={isMutating}
          editing={editing}
        />

        <DeleteConfirmDialog
          isOpen={!!rejectTarget}
          onClose={() => setRejectTarget(null)}
          onConfirm={handleRejectConfirm}
          title="Reject Quote"
          description={`Reject quote ${rejectTarget?.quote_number}? This cannot be undone.`}
          itemName={rejectTarget?.quote_number}
          isLoading={isMutating}
          destructive
        />

        <DeleteConfirmDialog
          isOpen={!!sendTarget}
          onClose={() => setSendTarget(null)}
          onConfirm={handleSendConfirm}
          title="Send Quote"
          description={`Mark quote ${sendTarget?.quote_number} as Sent?`}
          itemName={sendTarget?.quote_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Send"
        />

        <DeleteConfirmDialog
          isOpen={!!approveTarget}
          onClose={() => setApproveTarget(null)}
          onConfirm={handleApproveConfirm}
          title="Approve Quote"
          description={`Approve quote ${approveTarget?.quote_number}?`}
          itemName={approveTarget?.quote_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Approve"
        />

        <DeleteConfirmDialog
          isOpen={!!expireTarget}
          onClose={() => setExpireTarget(null)}
          onConfirm={handleExpireConfirm}
          title="Expire Quote"
          description={`Mark quote ${expireTarget?.quote_number} as Expired?`}
          itemName={expireTarget?.quote_number}
          isLoading={isMutating}
          destructive
        />

        <DeleteConfirmDialog
          isOpen={!!convertTarget}
          onClose={() => setConvertTarget(null)}
          onConfirm={handleConvertConfirm}
          title="Convert to Sales Order"
          description={`Convert quote ${convertTarget?.quote_number} to a Sales Order? This will create a new SO with all quote items.`}
          itemName={convertTarget?.quote_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Convert"
        />
      </div>

      <QuoteDetailSheet
        isOpen={!!viewTarget}
        onClose={() => setViewTarget(null)}
        quote={viewTarget}
      />
    </SidebarInset>
  );
}
